import React, { useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import { Formik, Form, Field } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import PageLoader from "../../components/Loader/PageLoader";
import DatePickerField from "../../components/FormikComponents/DatePickerField";
import Textarea from "../../components/Textarea/Textarea";
import styles from "./NewFinding.module.scss";
import Button from "../../components/Button/Button";
import Input from "../../components/Input/Input";
import FormikCheckbox from "../../components/FormikComponents/FormikCheckBox/FormikCheckBox";
import FormikUpload from "../../components/FormikComponents/FormikUpload/FormikUpload";
import Select from "../../components/Select/Select";
import * as Yup from "yup";
import axiosInstance from "../../utils/utils";
import { getOrganizationData } from "../../actions/organization";
import { ReactComponent as LockIcon } from "../../icons/lock.svg";
import _ from "lodash";
import Swal from "sweetalert2";
import { Trans, useTranslation } from "react-i18next";

const NewFinding = () => {
  const { orgId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [findingKey, setFindingKey] = useState(null);
  const formikRef = useRef(null);

  const sessionToken = localStorage.getItem("session_token");

  const { risksData, auditsTasksData, findingsData } = useSelector(
    (state) => state.organization,
  );

  const risksArray = Object.values(risksData);
  const sortedRisks = _.sortBy(risksArray, "risk_id");

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        description: Yup.string().required(
          t("newFindingPage.validation.descriptionRequired"),
        ),
        reference: Yup.string().required(
          t("newFindingPage.validation.referenceRequired"),
        ),
        chapter: Yup.string().required(
          t("newFindingPage.validation.chapterRequired"),
        ),
        risk: Yup.string().required(
          t("newFindingPage.validation.riskRequired"),
        ),
        attachments: Yup.array()
          .of(Yup.string())
          .required(t("newFindingPage.validation.attachmentsRequired")),
      }),
    [t],
  );

  const filteredRisks = sortedRisks.filter(
    (risk) => risk.level?.toLowerCase() !== "none",
  );

  const riskOptions = filteredRisks.map((risk) => ({
    id: risk.risk_num,
    reference: risk.risk_id,
    label: `${risk.risk_id} ${risk.description}`,
  }));

  const validChapterIds = new Set(filteredRisks.map((risk) => risk.qms_id));

  const chapterOptions = Array.from(validChapterIds).map((chapterId) => {
    const matchingRisk = sortedRisks.find((risk) => risk.qms_id === chapterId);
    return {
      id: chapterId,
      label: `${chapterId} ${matchingRisk?.qms_description || ""}`,
    };
  });

  const formatDate = (inputDate) => {
    if (!inputDate) {
      return new Date().toISOString().split("T")[0];
    }
    return new Date(inputDate).toISOString().split("T")[0];
  };

  const handleDescriptionBlur = async (descriptionValue, setFieldValue) => {
    if (!descriptionValue) return;

    try {
      const params = new URLSearchParams();
      params.append("description", descriptionValue);

      const requestUrl = `/ai/finding_check?${params.toString()}`;
      const response = await axiosInstance.get(requestUrl);

      if (response.data.chapter && response.data.chapter !== "N/A") {
        const chapterLabel = response.data.chapter;
        const chapterId = chapterLabel.split(" ")[0];

        const matchingRisk = riskOptions.find((risk) =>
          risk.reference.startsWith(chapterId),
        );

        // Only update if no risk is selected yet
        if (!formikRef.current?.values?.risk) {
          await setFieldValue("chapter", chapterLabel);
          if (matchingRisk) {
            await setFieldValue("risk", matchingRisk.label);
          }
        }
      }

      if (response.data.checklist) {
        setChecklist(response.data.checklist);
      }
    } catch (error) {
      console.error("Error checking description:", error);
    }
  };

  const handleSubmit = async (values, actions) => {
    try {
      const selectedRisk = riskOptions.find(
        (risk) => risk.label === values.risk,
      );
      const riskNumToSend = selectedRisk ? selectedRisk.id : null;

      if (!riskNumToSend) {
        actions.setSubmitting(false);
        setLoading(false);
        return;
      }

      const valuesToSend = {
        ...values,
        date: formatDate(values.date),
        risk: riskNumToSend,
      };

      const options = {
        method: "POST",
        url: `/nocodb/findings?org_id=${orgId}`,
        headers: { "session-token": localStorage.getItem("session_token") },
        data: [valuesToSend],
      };

      const response = await axiosInstance.request(options);

      const newFindingKey = response.data?.[0]; // Extract key from response

      if (newFindingKey) {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: t("newFindingPage.successToast", {
            findingKey: newFindingKey,
          }),
          showConfirmButton: false,
          timer: 3000,
          toast: true,
        });
        setFindingKey(newFindingKey);
      }

      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          true,
          false,
          true,
          false,
          false,
        ),
      );
      actions.resetForm();
    } catch (error) {
      console.error("Error submitting finding:", error);
    } finally {
      setLoading(false);
      actions.setSubmitting(false);
    }
  };

  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <PageLoader pageLoader text={t("loadingMessage")} />
    );
  }

  return (
    <MainLayout>
      <div className={styles.newFinding}>
        <h1>{t("newFindingPage.title")}</h1>
        {showPopup && (
          <div className={styles.popup}>
            <Trans
              i18nKey="newFindingPage.popupMessage"
              values={{ findingKey }}
              components={{ strong: <strong /> }}
            />
          </div>
        )}
        <Formik
          innerRef={formikRef}
          initialValues={{
            description: "",
            date: "",
            reference: "",
            attachments: [],
            risk: "",
            status: "Done",
            chapter: "",
            private: false,
          }}
          validationSchema={validationSchema}
          onSubmit={(values, actions) => {
            setLoading(true);
            handleSubmit(values, actions);
          }}
          enableReinitialize
        >
          {({ values, setFieldValue }) => {
            const chapterId = values.chapter.split(" ")[0];
            const filteredRiskOptions = riskOptions.filter((risk) =>
              risk.reference.startsWith(chapterId),
            );

            return (
              <Form className={styles.form}>
                <div className={styles.description}>
                  <label htmlFor="description">
                    {t("newFindingModal.description.label")}
                  </label>
                  <Field
                    name="description"
                    component={Textarea}
                    onBlur={(e) =>
                      handleDescriptionBlur(
                        e.target.value,
                        formikRef.current.setFieldValue,
                      )
                    }
                  />
                </div>
                <div className={styles.row}>
                  <div className={styles.date}>
                    <label htmlFor="date">
                      {t("newFindingModal.date.label")}
                    </label>
                    <Field name="date" component={DatePickerField} />
                  </div>
                  <div className={styles.reference}>
                    <label htmlFor="reference">
                      {t("newFindingModal.reference.label")}
                    </label>
                    <Field name="reference" component={Input} />
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.chapter}>
                    <label htmlFor="chapter">
                      {t("newFindingPage.chapter.label")}
                    </label>
                    <Field
                      name="chapter"
                      component={Select}
                      text={t("newFindingPage.chapter.placeholder")}
                      options={chapterOptions}
                      onClick={() => setFieldValue("risk", "")}
                    />
                  </div>
                  <div className={styles.risk}>
                    <label htmlFor="risk">
                      {t("newFindingPage.risk.label")}
                    </label>
                    <Field
                      name="risk"
                      component={Select}
                      text={t("newFindingModal.risk.placeholder")}
                      options={filteredRiskOptions}
                      disabled={!values.chapter}
                    />
                  </div>
                </div>
                <FormikCheckbox
                  name="status"
                  label={t("newFindingModal.status.label")}
                  trueValue="Open"
                  falseValue="Done"
                />
                <div className={styles.privateContainer}>
                  <FormikCheckbox
                    name="private"
                    label={t("newFindingModal.private.label")}
                    trueValue={true}
                    falseValue={false}
                  />
                  <LockIcon className={styles.lockIcon} />
                </div>
                <div className={styles.attachment}>
                  <label htmlFor="attachments">
                    {t("newFindingModal.attachments.label")}
                  </label>
                  <Field
                    name="attachments"
                    component={FormikUpload}
                    greenIcon
                  />
                </div>
                <Button
                  text={t("newFindingPage.submitButton")}
                  type="submit"
                  disabled={loading}
                  className={styles.submitButton}
                />
              </Form>
            );
          }}
        </Formik>
      </div>
    </MainLayout>
  );
};

export default NewFinding;
